import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { VideoPlayer } from "../components/VideoPlayer";
import { GraduationCap, PlayCircle, MagnifyingGlass, Eye, LockSimple, CheckCircle } from "@phosphor-icons/react";

const ROLE_TAGS = ["all", "server", "bartender", "barista", "line_cook", "housekeeping"];

export default function AcademyPage() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");
  const [playing, setPlaying] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [watches, setWatches] = useState({});
  const [quizStarted, setQuizStarted] = useState(false);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/videos/my-watches").then((r) => setWatches(r.data)).catch(() => {});
  }, []);

  const onProgress = (id, pct) => {
    setWatches((w) => {
      const cur = w[id]?.max_pct || 0;
      return pct > cur ? { ...w, [id]: { max_pct: pct, completed: pct >= 80 } } : w;
    });
  };

  const filtered = videos.filter((v) =>
    (tag === "all" || v.role_tag === tag || v.role_tag === "all") &&
    (!q || v.title.toLowerCase().includes(q.toLowerCase()) || v.category.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-8 rise-in max-w-5xl mx-auto" data-testid="academy-page">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center">
          <GraduationCap size={30} weight="duotone" className="text-emerald-400" />
        </div>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Learning Hub</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Academy</h1>
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlass size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="academy-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search training videos…"
            className="w-full h-16 pl-14 pr-5 rounded-2xl border border-slate-200 bg-white text-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {ROLE_TAGS.map((t) => (
            <button key={t} data-testid={`academy-tag-${t}`} onClick={() => setTag(t)}
              className={`h-11 px-5 rounded-full text-sm font-bold whitespace-nowrap capitalize tap-press border ${
                tag === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {t === "all" ? "All roles" : t.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6" data-testid="academy-grid">
        {filtered.map((v) => (
          <button key={v.id} data-testid={`academy-video-${v.id}`} onClick={() => { setPlaying(v); setRevealed({}); setQuizStarted(false); }}
            className="text-left bg-white border border-slate-200 rounded-2xl overflow-hidden card-lift tap-press">
            <div className="relative h-44 bg-slate-900 flex items-center justify-center">
              <PlayCircle size={64} weight="duotone" className="text-white/90" />
              {watches[v.id]?.completed && (
                <span data-testid={`academy-watched-badge-${v.id}`}
                  className="absolute top-3 right-3 text-[11px] font-bold bg-emerald-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1">
                  <CheckCircle size={14} weight="fill" /> Watched
                </span>
              )}
            </div>
            <div className="p-6">
              <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">
                {v.category} · {v.role_tag === "all" ? "All roles" : v.role_tag.replace("_", " ")}
              </span>
              <h3 className="font-heading font-bold text-xl mt-1 leading-snug">{v.title}</h3>
              {v.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{v.description}</p>}
            </div>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-slate-400 col-span-full">No videos match — try another role or search term.</p>}
      </div>

      {playing && (() => {
        const pct = Math.round(watches[playing.id]?.max_pct || 0);
        const unlocked = pct >= 80;
        return (
          <VideoPlayer video={playing} onClose={() => setPlaying(null)} onProgress={onProgress}>
            {playing.quiz?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Test yourself</p>
                  <span data-testid="academy-watch-pct" className={`text-xs font-bold ${unlocked ? "text-emerald-600" : "text-slate-400"}`}>{pct}% watched</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
                  <div className={`h-full rounded-full ${unlocked ? "bg-emerald-500" : "bg-amber-400"}`}
                    style={{ width: `${pct}%`, transition: "width 400ms ease" }} />
                </div>
                {!quizStarted ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <button data-testid="academy-start-quiz" disabled={!unlocked} onClick={() => setQuizStarted(true)}
                      className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
                      {unlocked ? <PlayCircle size={18} weight="duotone" /> : <LockSimple size={18} />} Start quiz
                    </button>
                    {!unlocked && (
                      <p data-testid="academy-quiz-locked-note" className="text-xs font-semibold text-slate-500">
                        Watch at least 80% of the video to unlock the quiz.
                      </p>
                    )}
                  </div>
                ) : (
                  playing.quiz.map((qz, i) => (
                    <div key={qz.id} className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <p className="font-bold text-base">{i + 1}. {qz.question}</p>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {qz.options.map((o, j) => (
                          <span key={j} className={`text-sm px-3 py-2.5 rounded-lg border ${
                            revealed[qz.id] && j === qz.correct_index
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold"
                              : "border-slate-200 bg-white text-slate-600"}`}>{o}</span>
                        ))}
                      </div>
                      {!revealed[qz.id] && (
                        <button data-testid={`academy-reveal-${qz.id}`} onClick={() => setRevealed({ ...revealed, [qz.id]: true })}
                          className="mt-3 h-10 px-4 rounded-full border border-slate-300 text-xs font-bold text-slate-600 flex items-center gap-2 tap-press hover:border-slate-900">
                          <Eye size={16} /> Reveal answer
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </VideoPlayer>
        );
      })()}
    </div>
  );
}
